import { colors, intl, ui, UserProfile } from '@beelday/common'
import styled from '@emotion/styled'
import { FC, ReactElement } from 'react'
import { KnowledgeCheckReview } from '../../knowledge-check'
import { questionSummary } from '../../model/question-summary'
import { reviewActions } from '../../model/review-actions'
import { answerRows } from '../../model/review-answers'
import KnowledgeCheckHeader from '../knowledge-check-header'
import AnswerBars from './answer-bars'
import QuestionSummaryPanel from './question-summary-panel'
import StageCard from './stage-card'

const Eyebrow = styled.div`
	color: ${colors.warmGrey};
	font-size: 20px;
	font-weight: 500;

	${ui.responsive.desktopFHD} {
		font-size: 24px;
	}
`

const Question = styled.h2`
	color: ${colors.blackish};
	font-size: 28px;
	font-weight: 700;
	line-height: 1.25;
	margin: 6px 0 36px;

	${ui.responsive.desktopFHD} {
		font-size: 36px;
	}
`

type Props = {
	userProfile: UserProfile | undefined | null
	review: KnowledgeCheckReview
	onClose: () => void
	onPrev: () => void
	onNext: () => void
}

/**
 * The trainer's walk through the answers, one question at a time, on the
 * in-person stage: the question and how many picked each answer, and beside
 * them how the room did on it as a whole. The online card is
 * `KnowledgeCheckTrainerReviewGridView`.
 */
const KnowledgeCheckTrainerReviewStageView: FC<Props> = ({
	userProfile,
	review,
	onClose,
	onPrev,
	onNext,
}): ReactElement => {
	const t = intl.useTranslate()
	const index = review.currentQuestionIndex
	const question = review.questions[index]
	const actions = reviewActions(index, review.questions.length)
	return (
		<StageCard
			header={
				<KnowledgeCheckHeader
					title={review.name}
					userProfile={userProfile}
					progress={{
						currentValue: index + 1,
						maxValue: review.questions.length,
					}}
				/>
			}
			sideLabel={t('knowledge_check.stage.summary')}
			side={
				<QuestionSummaryPanel
					summary={questionSummary(
						question,
						review.users.length,
						review.useCorrectAnswers
					)}
				/>
			}
			startActions={
				actions.closeFirst ? (
					<ui.ButtonMinor onClick={onClose}>
						<intl.Translate>knowledge_check.review.close</intl.Translate>
					</ui.ButtonMinor>
				) : null
			}
			endActions={
				<>
					{actions.previous ? (
						<ui.ButtonMinor onClick={onPrev}>
							<intl.Translate>
								knowledge_check.review.prev_question
							</intl.Translate>
						</ui.ButtonMinor>
					) : null}
					{actions.primary === 'next' ? (
						<ui.ButtonPrimary onClick={onNext}>
							<intl.Translate>
								knowledge_check.review.next_question
							</intl.Translate>
						</ui.ButtonPrimary>
					) : (
						<ui.ButtonPrimary onClick={onClose}>
							<intl.Translate>knowledge_check.review.close</intl.Translate>
						</ui.ButtonPrimary>
					)}
				</>
			}
		>
			<Eyebrow>
				<intl.Translate count={index + 1}>
					knowledge_check.review.question_count
				</intl.Translate>
			</Eyebrow>
			<Question>{question.question}</Question>
			<AnswerBars
				rows={answerRows(
					question,
					review.users.length,
					review.useCorrectAnswers
				)}
				correctLabel={t('knowledge_check.stage.correct_answer')}
			/>
		</StageCard>
	)
}

export default KnowledgeCheckTrainerReviewStageView
